import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, alpha } from "@mui/material";
import { ScheduleOutlined } from "@mui/icons-material";
import { RootState, useAppDispatch } from "@store/store";
import { setLoanStatus } from "@store/slices/registerSlice";
import { useLazyGetLoanApplicationByIdQuery } from "@api/applications";
import { LoanStatus } from "@interfaces/catalogs";
import useInterval from "@hooks/useInterval";
import { STATUS_CATALOG } from "@helpers/constantes";

/** Mismo periodo de sondeo que las salas de espera legacy. */
const SECONDS_TO_FETCH = 10;

/**
 * Tonos propios de esta pantalla del diseño Pymio (`S_SolicitudConcluida`), que
 * no tienen equivalente en la paleta del theme: el borde frío de la card y los
 * dos grises azulados del copy. El navy, el ámbar y el verde sí salen del theme
 * para que la pantalla siga al tenant.
 */
const CARD_BORDER = "#EAF1F7";
const TEXT_BLUE_GRAY = "#5A7BA6";
const TEXT_MUTED = "#7E90A4";

/**
 * Sala de espera del marketplace: la solicitud ya se envió y las financieras
 * aliadas están evaluando el perfil.
 *
 * Sondea la solicitud igual que `WaitingRoomMarketPlace`/`DefaultWaitingRoom` del
 * legacy: no hay un "siguiente paso" fijo que calcular con `useStatusConfig`,
 * porque el desenlace se ramifica (oferta, rechazo, revisión) y solo el backend
 * sabe cuál tocó.
 *
 * No usa `PageHeader` ni `Paper variant="lendia_paper"` a propósito: el diseño
 * la resuelve como una card centrada de celebración (radio 24, sombra alta,
 * padding 56/64) en vez del header con barra lateral y la card estándar.
 */
export const WaitingRoomConcluida = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname, search } = useLocation();

  const companyData = useSelector((state: RootState) => state.register.company);
  const application = useSelector((state: RootState) => state.register.application);
  const statusConfig = useSelector((state: RootState) => state.app.status_config);
  const [getLoanApplication] = useLazyGetLoanApplicationByIdQuery();

  const syncStatus = async () => {
    try {
      const loan_application = await getLoanApplication(application.id).unwrap();
      const statusCode = loan_application?.status_catalog?.code || loan_application?.current_status?.code || "";

      if (!statusCode) return;

      dispatch(setLoanStatus(statusCode as LoanStatus));

      // Mientras el backend siga reportando la sala de espera no hay a dónde ir.
      // El legacy no tiene este guard: se apoya solo en comparar URLs, que ahí
      // funciona porque legacy y `status_config` comparten prefijo. En el wizard
      // nuevo la comparación no basta (ver el TODO de abajo).
      if (statusCode === STATUS_CATALOG.waiting_room.code) return;

      // TODO: `status_config` debe devolver URLs del wizard nuevo (`/v2/producto/...`).
      // Si sigue devolviendo las legacy (`/producto/creditosimple/...`), este navigate
      // saca al usuario del wizard — mismo supuesto ya documentado para `nextStatus.url`
      // en el resto de las pantallas migradas.
      const targetUrl =
        statusConfig.find((item) => item.code === statusCode)?.url ||
        loan_application?.status_catalog?.url_actual ||
        loan_application?.current_status?.url ||
        "";

      if (!targetUrl) return;

      // Si ya estamos en la misma ruta, no navegamos (evita side-effects innecesarios).
      if (targetUrl !== `${pathname}${search}`) {
        navigate(targetUrl, { replace: true });
      }
    } catch (error) {
      console.error("Ha ocurrido un error al obtener la aplicación", error);
    }
  };

  const loanInterval = useInterval(syncStatus, SECONDS_TO_FETCH);

  useEffect(() => {
    loanInterval.setIsActiveInterval(true);

    return () => {
      loanInterval.setIsActiveInterval(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ maxWidth: 860, mx: "auto" }}>
      <Box
        sx={{
          bgcolor: "background.paper",
          border: `1px solid ${CARD_BORDER}`,
          borderRadius: "24px",
          boxShadow: "0 18px 50px rgba(6, 44, 78, 0.10)",
          p: { xs: "40px 24px", sm: "56px 64px" },
          textAlign: "center",
        }}
      >
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "8px", mb: "22px" }}>
          <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: "success.main" }} />
          <Typography sx={{ fontWeight: 700, fontSize: 13, letterSpacing: "0.14em", color: "success.main" }}>
            SOLICITUD CONCLUIDA
          </Typography>
        </Box>

        <Typography
          component="h1"
          sx={{ fontWeight: 600, fontSize: 38, lineHeight: 1.18, color: "primary.dark", m: "0 0 20px" }}
        >
          ¡Felicidades, {companyData.name}!
          <br />
          Concluiste tu solicitud
        </Typography>

        <Typography sx={{ fontSize: 17, lineHeight: 1.6, color: TEXT_BLUE_GRAY, maxWidth: 620, m: "0 auto 8px" }}>
          Tu solicitud ya está en el Marketplace de Pymio by Lendia. Nuestras instituciones financieras aliadas
          evaluarán tu perfil bajo condiciones equitativas.
        </Typography>

        {/*
         * Orbe de espera: dos anillos fijos, un barrido cónico que gira (`pyorbit`)
         * y un halo que late hacia afuera (`pypulse`) detrás del núcleo sólido.
         */}
        <Box
          sx={(theme) => ({
            position: "relative",
            width: 200,
            height: 200,
            m: "28px auto 24px",
            "@keyframes pyorbit": {
              from: { transform: "rotate(0deg)" },
              to: { transform: "rotate(360deg)" },
            },
            "@keyframes pypulse": {
              "0%": { transform: "translate(-50%, -50%) scale(.7)", opacity: 0.55 },
              "70%": { opacity: 0 },
              "100%": { transform: "translate(-50%, -50%) scale(1.35)", opacity: 0 },
            },
            "& > *": { position: "absolute", borderRadius: "50%" },
            "& .orb-centered": { top: "50%", left: "50%", transform: "translate(-50%, -50%)" },
            "& .orb-ring-outer": { inset: 0, border: `1.5px solid ${alpha(theme.palette.primary.main, 0.28)}` },
            "& .orb-ring-inner": { inset: "34px", border: `1px solid ${alpha(theme.palette.primary.main, 0.19)}` },
          })}
        >
          <Box className="orb-ring-outer" />
          <Box className="orb-ring-inner" />
          <Box
            sx={(theme) => ({
              inset: 0,
              animation: "pyorbit 2.2s linear infinite",
              background: `conic-gradient(from 0deg, ${alpha(theme.palette.primary.main, 0)} 0deg, ${alpha(
                theme.palette.primary.main,
                0.32,
              )} 160deg, ${alpha(theme.palette.primary.main, 0)} 161deg)`,
            })}
          />
          <Box
            className="orb-centered"
            sx={(theme) => ({
              width: 132,
              height: 132,
              animation: "pypulse 1.6s ease-out infinite",
              bgcolor: alpha(theme.palette.primary.main, 0.16),
            })}
          />
          <Box
            className="orb-centered"
            sx={(theme) => ({
              width: 72,
              height: 72,
              bgcolor: "primary.main",
              boxShadow: `0 8px 22px ${alpha(theme.palette.primary.main, 0.4)}`,
            })}
          />
        </Box>

        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            gap: "14px",
            justifyContent: "center",
            maxWidth: 560,
            m: "0 auto",
            p: "18px 22px",
            bgcolor: alpha(theme.palette.primary.main, 0.09),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
            borderRadius: "14px",
          })}
        >
          <ScheduleOutlined sx={{ fontSize: 22, color: "primary.main", flexShrink: 0 }} />
          <Typography sx={{ fontSize: 15.5, lineHeight: 1.45, color: "primary.dark", textAlign: "left" }}>
            En cuanto una financiera emita la <strong>primera oferta</strong>, pasarás a tu comparativa para verla.
          </Typography>
        </Box>

        <Typography sx={{ fontSize: 14.5, lineHeight: 1.55, color: TEXT_MUTED, maxWidth: 520, m: "24px auto 0" }}>
          Te avisaremos aquí mismo y vía WhatsApp. Las demás financieras podrán seguir ofertando; te recomendamos
          esperar al menos{" "}
          <Box component="strong" sx={{ color: "primary.dark" }}>
            2 días
          </Box>{" "}
          para darles tiempo a todas. Puedes cerrar esta ventana con tranquilidad.
        </Typography>
      </Box>
    </Box>
  );
};
